import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import admin components (lazy-loaded for performance)
const AdminDashboard = React.lazy(() => import("../AdminDashboard"));
const BillChaserPage = React.lazy(() => import("./billChaserPage"));

/**
 * Admin Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (admin/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/admin/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const adminModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "admin",
    name: "System Administration",
    description: "System-wide administrative tools and automation",
    version: "0.0.1",
    enabled: true,
    category: "administration",
    order: 100, // High order to appear at the end
    icon: "⚙️",
    color: "#718096",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/admin",
            component: AdminDashboard,
            permissionLevel: "view",
        },
        {
            path: "/bill-chaser",
            component: BillChaserPage,
            permissionLevel: "edit",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "System Dashboard",
            path: "/admin",
            icon: "📊",
            permissionLevel: "view",
        },
        {
            label: "Bill Chaser",
            path: "/bill-chaser",
            icon: "💸",
            permissionLevel: "edit",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/admin/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Bill Automation",
            path: "/bill-chaser",
            icon: "💸",
            description: "Manage automated bill reminders"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready system administration",
                "✅ Complete tenant configuration management",
                "✅ Multi-service API key management",
                "✅ Bill automation and chaser system",
                "✅ Module deployment tools",
                "✅ Advanced integration support (Stripe, Twilio, Vapi)"
            ],
            notes: "Official stable release with comprehensive system administration capabilities."
        },
        {
            version: "0.8.0",
            releaseDate: "2024-09-20",
            title: "Theme System Migration",
            features: [
                "✨ Full light/dark mode support",
                "✨ Enhanced CreateTenant with theme awareness (52 lines)",
                "✨ Updated DeploymentManager color modes (12 lines)",
                "✨ Consistent visual design system"
            ],
            notes: "Complete theme system implementation across admin pages."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-08-26",
            title: "Twilio & Phone System Integration",
            features: [
                "✨ Added TwiML Application SID field (41 lines)",
                "✨ Enhanced phone system configuration",
                "✨ Improved tenant communication setup",
                "✨ Better API key management"
            ],
            notes: "Advanced phone system integration capabilities."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-08-24",
            title: "Vapi Voice AI & API Management",
            features: [
                "✨ Added Vapi Voice AI configuration (537 insertions)",
                "✨ Enhanced Twilio API key management (145 lines)",
                "✨ Improved form field editing logic (46 lines)",
                "✨ Browser calling feature support (106 lines)",
                "✨ Better service integration UI"
            ],
            notes: "Comprehensive AI voice assistant integration and API management."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-23",
            title: "Knowledge Base & Phone System Support",
            features: [
                "✨ Added Twilio credentials fields (69 lines)",
                "✨ Knowledge Base module integration (23 lines)",
                "✨ Enhanced DeploymentManager styling",
                "✨ Improved admin dashboard consistency"
            ],
            notes: "Knowledge base and phone system configuration features."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-21",
            title: "Enhanced Tenant Configuration",
            features: [
                "✨ Added alphaId and dedicatedNumber fields (56 lines)",
                "✨ Enhanced TenantsList with improved UI (307 insertions)",
                "✨ Implemented useAlphaId feature (33 lines)",
                "✨ Better tenant identification system"
            ],
            notes: "Advanced tenant identification and configuration options."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-13",
            title: "Stripe Integration & UI Improvements",
            features: [
                "✨ Added Stripe webhook secret field (51 lines)",
                "✨ Enhanced tenant name interaction (11 lines)",
                "✨ Improved payment processing integration",
                "✨ Better webhook security"
            ],
            notes: "Stripe payment integration and webhook management."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-07-27",
            title: "Module Management & Research Features",
            features: [
                "✨ Added Lemonfox API key integration (30 lines)",
                "✨ Enhanced module management features (153 insertions)",
                "✨ Improved TenantsList with module visibility (468 insertions)",
                "✨ Better module deployment tools",
                "✨ Research & Design module support"
            ],
            notes: "Enhanced module management and third-party service integrations."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-06-14",
            title: "Initial System Administration",
            features: [
                "✅ Created TenantManagement component (427 lines)",
                "✅ Created DeploymentManager component (378 lines)",
                "✅ Created CreateTenant page (759 lines)",
                "✅ Created EditTenant page (744 lines)",
                "✅ Created TenantsList component (658 lines)",
                "✅ Created ModuleDeployment page (182 lines)",
                "✅ Implemented TenantDeploymentManager (766 lines)",
                "✅ Created Bill Chaser automation (762 lines)",
                "✅ AI text improvement features",
                "✅ Module enable/disable functionality",
                "✅ Comprehensive tenant configuration"
            ],
            notes: "Initial implementation of system administration with 805+ lines in first commit, expanded to 7,000+ lines across multiple components."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default adminModuleConfig;